import { useState, useEffect } from 'react';
import { propertyCanHaveDefaultValue, getDefaultValue, getOwnerFullName } from 'data-token-picker/utils/properties';
import FallbackField from 'data-token-picker/components/FallbackField';
import FallbackUserField from 'data-token-picker/components/FallbackUserField';
import { PropertyOnlyCategory } from 'data-token-picker/constants/Properties';
import { jsx as _jsx } from "react/jsx-runtime";
const isSaveEnabled = (allowEmptyFallbacks, isGlobalFallback, hasFallback) => {
  return allowEmptyFallbacks || isGlobalFallback || hasFallback;
};
const findNonGroupCategory = categories => {
  if (!categories || !categories.length) {
    return undefined;
  }
  let validCategory;
  for (let i = 0; !validCategory && i < categories.length; ++i) {
    validCategory = categories[i].value || findNonGroupCategory(categories[i].options);
  }
  return validCategory;
};
export const useBrowserManagement = props => {
  const {
    fallback: initialFallback,
    field: initialField,
    fieldsByCategory,
    hideFallbacks,
    allowEmptyFallbacks,
    onConfirm,
    settingsLink,
    category: initialCategory,
    categories
  } = props;
  const defaultCategory = findNonGroupCategory(categories);
  const [category, setCategory] = useState(initialCategory || defaultCategory);
  const [fieldReference, setFieldReference] = useState();
  const [parentFieldReference, setParentFieldReference] = useState();
  const [fallback, setFallback] = useState(initialFallback);
  const [isGlobalFallback, setIsGlobalFallback] = useState(initialFallback === undefined);
  const [canSave, setCanSave] = useState(false);
  const parts = ((fieldReference === null || fieldReference === void 0 ? void 0 : fieldReference.value) || '').replace(`${category}.`, '').split('.');
  let selectedUserField;
  if (fieldReference !== null && fieldReference !== void 0 && fieldReference.isOwnerField) {
    selectedUserField = parts.pop();
  }
  const selectedField = parts.pop();
  const isOwnerField = Boolean(fieldReference === null || fieldReference === void 0 ? void 0 : fieldReference.isOwnerField);
  const defaultValue = getDefaultValue({
    isOwnerField,
    fieldReference,
    parentFieldReference
  });
  const canHaveFallbacks = !hideFallbacks && propertyCanHaveDefaultValue({
    category,
    name: selectedField,
    isOwnerField
  }, selectedUserField);
  useEffect(() => {
    const path = initialCategory !== PropertyOnlyCategory ? `${initialCategory}.${initialField}` : initialField;
    const findCurrent = fieldNode => {
      if (fieldNode.value === path) {
        setFieldReference(fieldNode);
        return true;
      }
      if (fieldNode.children) {
        setParentFieldReference(fieldNode);
        fieldNode.children.some(findCurrent);
      }
      return false;
    };
    if (fieldsByCategory !== null && fieldsByCategory !== void 0 && fieldsByCategory[initialCategory]) {
      fieldsByCategory[initialCategory].some(findCurrent);
    }
  }, [initialCategory, initialField, fieldsByCategory]);
  useEffect(() => {
    setCanSave(!!fieldReference && (hideFallbacks || isSaveEnabled(!!allowEmptyFallbacks, isGlobalFallback, !!fallback)));
  }, [fieldReference, allowEmptyFallbacks, hideFallbacks, isGlobalFallback, fallback, defaultValue]);
  useEffect(() => {
    /**
     * IF there is a default value AND no local fallback
     * OR this field cannot have a local fallback value,
     * THEN it must be a global fallback.
     */
    setIsGlobalFallback(!!defaultValue && !fallback || !canHaveFallbacks);
  }, [defaultValue, fallback, canHaveFallbacks]);
  const handleEdit = ({
    category: updatedCategory,
    fieldOption
  }) => {
    setCategory(updatedCategory);
    setFieldReference(fieldOption);
    setFallback('');
  };
  const handleFallbackChanged = updatedFallback => {
    setFallback(updatedFallback);
    setCanSave(!!fieldReference && isSaveEnabled(!!allowEmptyFallbacks, isGlobalFallback, !!updatedFallback));
  };
  const handleIsGlobalFallbackChanged = updatedIsGlobalFallback => {
    setIsGlobalFallback(updatedIsGlobalFallback);
    setCanSave(!!fieldReference && isSaveEnabled(!!allowEmptyFallbacks, updatedIsGlobalFallback, !!fallback));
  };
  const handleConfirm = () => {
    if (!fieldReference || !category || !selectedField) {
      return;
    }
    const excludeFallback = isGlobalFallback || hideFallbacks;
    const fallbackValue = excludeFallback ? null : fallback || '';
    onConfirm({
      category,
      name: selectedField,
      originalProperty: {
        fieldType: fieldReference.fieldType
      },
      isHubSpotDefined: !!fieldReference.hubspotDefined,
      text: fieldReference.text,
      path: fieldReference.value
    }, selectedUserField, fallbackValue, !!fieldReference.hubspotDefined);
  };
  let MaybeUserFallbackComponent;
  if (isOwnerField) {
    const ownerFullName = getOwnerFullName({
      parentFieldReference
    });
    MaybeUserFallbackComponent = /*#__PURE__*/_jsx(FallbackUserField, {
      value: ownerFullName || (parentFieldReference === null || parentFieldReference === void 0 ? void 0 : parentFieldReference.fieldDefaultValue),
      settingsLink: settingsLink
    });
  }
  let MaybeFallbackComponent;
  if (canHaveFallbacks) {
    const calculatedFallback = isGlobalFallback ? defaultValue : fallback;
    const showGlobalFallbackCheckbox = !!defaultValue;
    MaybeFallbackComponent = /*#__PURE__*/_jsx(FallbackField, {
      fallbackValue: calculatedFallback,
      onFallbackChange: handleFallbackChanged,
      isGlobalFallback: isGlobalFallback,
      onIsGlobalFallbackChange: handleIsGlobalFallbackChanged,
      showGlobalFallbackCheckbox: showGlobalFallbackCheckbox,
      settingsLink: settingsLink
    });
  }
  return {
    canSave,
    category,
    fieldReference,
    fieldsByCategory,
    handleConfirm,
    handleEdit,
    MaybeUserFallbackComponent,
    MaybeFallbackComponent
  };
};